/**
 * Swiper-based replacement for the product detail page's thumbnail strip
 * (previously native scroll-snap driven by the `slider-component` custom
 * element). Only used when section.settings.gallery_layout == 'thumbnail_slider'
 * (see snippets/product-media-gallery.liquid) - the grid/legacy layouts keep
 * using slider-component untouched.
 *
 * Exposes `root.thumbnailSwiper = { swiper, scrollToActive(item) }` so
 * media-gallery.js can keep the active thumbnail in view without depending
 * on slider-component's API.
 */
(() => {
  const SELECTOR = "[data-thumbnail-swiper]";
  const MQL = window.matchMedia("(min-width: 780px)");

  function getStep(root) {
    const button = root.querySelector("[data-step]");
    const step = button && parseInt(button.dataset.step, 10);
    return Number.isFinite(step) && step > 0 ? step : 1;
  }

  function updateNavState(swiper, prevBtn, nextBtn) {
    if (prevBtn) prevBtn.disabled = swiper.isBeginning;
    if (nextBtn) nextBtn.disabled = swiper.isEnd;
  }

  function createSwiper(root, isDesktop) {
    const viewport = root.querySelector(".thumbnail-list.swiper");
    if (!viewport || viewport.querySelectorAll(".swiper-slide").length === 0) {
      return null;
    }

    const prevBtn = root.querySelector(".slider-button--prev");
    const nextBtn = root.querySelector(".slider-button--next");
    const step = getStep(root);

    return new window.Swiper(viewport, {
      direction: isDesktop ? "vertical" : "horizontal",
      slidesPerView: isDesktop ? 4.5 : 1,
      slidesPerGroup: step,
      spaceBetween: isDesktop ? 16 : 0,
      watchOverflow: true,
      watchSlidesProgress: true,
      grabCursor: true,
      observer: true,
      observeParents: true,
      navigation: prevBtn && nextBtn ? { prevEl: prevBtn, nextEl: nextBtn } : false,
      on: {
        init: (swiper) => updateNavState(swiper, prevBtn, nextBtn),
        slideChange: (swiper) => updateNavState(swiper, prevBtn, nextBtn),
        reachBeginning: (swiper) => updateNavState(swiper, prevBtn, nextBtn),
        reachEnd: (swiper) => updateNavState(swiper, prevBtn, nextBtn),
        resize: (swiper) => updateNavState(swiper, prevBtn, nextBtn),
      },
    });
  }

  function bind(root) {
    if (root.dataset.thumbnailSwiperBound) return;
    root.dataset.thumbnailSwiperBound = "true";
    if (typeof window.Swiper === "undefined") return;

    let isDesktop = MQL.matches;
    let swiper = createSwiper(root, isDesktop);

    root.thumbnailSwiper = {
      get swiper() {
        return swiper;
      },
      scrollToActive(item) {
        if (!swiper || !item) return;
        const index = Array.prototype.indexOf.call(swiper.slides, item);
        if (index === -1) return;
        if (item.classList.contains("swiper-slide-visible")) return;
        swiper.slideTo(index);
      },
    };

    MQL.addEventListener("change", (event) => {
      isDesktop = event.matches;
      if (swiper) swiper.destroy(true, true);
      swiper = createSwiper(root, isDesktop);
    });
  }

  function apply(scope = document) {
    scope.querySelectorAll(SELECTOR).forEach(bind);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => apply());
  } else {
    apply();
  }

  document.addEventListener("shopify:section:load", (event) => apply(event.target));
})();
